import { useCallback, useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';

import { isInAudience } from '../audience/audience';
import { useViewer } from '../audience/useViewer';
import { toLocalDay } from '../days';
import {
  type ReleaseSurface,
  useProductExperiences,
} from '../state/ProductExperiencesProvider';

import { releaseEntries } from './releaseCatalog';
import {
  type LocalizedRelease,
  isReleaseUnread,
  localizeRelease,
} from './releaseEntries';

// QA: `?release_preview=<id>` shows that entry as unread to anyone, whatever
// its audience or date, until the parameter is removed.
export const RELEASE_PREVIEW_PARAM = 'release_preview';

export type FeedRelease = LocalizedRelease & {
  // False only in the admin "all entries" view, for entries outside the
  // admin's own audience (or not live yet).
  isForViewer: boolean;
};

export type ReleaseFeed = {
  releases: FeedRelease[];
  unreadIds: ReadonlySet<string>;
  markSeen: (ids: readonly string[], surface: ReleaseSurface) => void;
};

export const useReleaseFeed = ({
  includeAll = false,
}: { includeAll?: boolean } = {}): ReleaseFeed => {
  const { me } = useAppData();
  const { language } = useLocale();
  const { search } = useLocation();
  const { ready, state, markReleasesSeen } = useProductExperiences();
  const previewId = new URLSearchParams(search).get(RELEASE_PREVIEW_PARAM);

  const viewer = useViewer();

  const accountCreatedDay =
    me?.created_at == null ? null : toLocalDay(new Date(me.created_at));

  const { releases, unreadIds } = useMemo(() => {
    const today = toLocalDay(new Date());
    const visible: FeedRelease[] = [];
    const unread = new Set<string>();
    if (viewer == null) {
      return { releases: visible, unreadIds: unread };
    }
    for (const entry of releaseEntries) {
      const isPreview = entry.id === previewId;
      // No date gate: an entry ships with its feature, so a deploy preview
      // shows the next release's entries before their date.
      const isForViewer = isPreview || isInAudience(entry.meta, viewer);
      if (!isForViewer && !includeAll) {
        continue;
      }
      visible.push({ ...localizeRelease(entry, language), isForViewer });
      const isUnread =
        isPreview ||
        (isForViewer &&
          ready &&
          isReleaseUnread(entry.meta, {
            seen: state.seenReleases[entry.id] != null,
            today,
            accountCreatedDay,
          }));
      if (isUnread) {
        unread.add(entry.id);
      }
    }
    return { releases: visible, unreadIds: unread };
  }, [
    viewer,
    language,
    previewId,
    includeAll,
    ready,
    state,
    accountCreatedDay,
  ]);

  const markSeen = useCallback(
    (ids: readonly string[], surface: ReleaseSurface) =>
      markReleasesSeen(
        ids.filter(id => id !== previewId),
        surface,
      ),
    [markReleasesSeen, previewId],
  );

  return { releases, unreadIds, markSeen };
};
